import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { Btn } from '../components/Btn.tsx';
import { Modal } from '../components/Modal.tsx';
import { Field, Select, TextInput } from '../components/Field.tsx';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { ErrorMessage } from '../components/ErrorMessage.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { ProgressBar } from '../components/ProgressBar.tsx';
import { OwnerFilterBar } from '../components/OwnerFilterBar.tsx';
import { OwnerPicker } from '../components/OwnerPicker.tsx';
import { filterByOwner, type OwnerFilterCriteria } from '../lib/ownerFilter.ts';
import { COMPANY_STATUSES, COMPANY_STATUS_LABELS } from '../lib/companyStatus.ts';
import { companyInfoLabel, contractorTypeOptions } from '../lib/companyInfo.ts';
import { teamRegionNames, useTeamRegions } from '../lib/teamRegions.ts';
import { useAdminAuth } from '../lib/auth/admin.tsx';
import { useBranding } from '../lib/branding.tsx';
import type { Driver, Company } from '../lib/types.ts';

const STATUS_OPTIONS = COMPANY_STATUSES.map((s) => ({ value: s, label: COMPANY_STATUS_LABELS[s] }));

export function AdminDrivers() {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [criteria, setCriteria] = useState<OwnerFilterCriteria>({
    ownerIds: [],
    statuses: [],
    query: '',
  });

  const { activeTeamRegion } = useAdminAuth();
  const driversQuery = useQuery({
    queryKey: ['drivers', 'all', { region: activeTeamRegion }],
    queryFn: () =>
      adminApi.get<{ drivers: Driver[] }>(
        activeTeamRegion
          ? `/api/drivers?team_region=${encodeURIComponent(activeTeamRegion)}`
          : '/api/drivers',
      ),
  });
  const companiesQuery = useQuery({
    queryKey: ['companies', { region: activeTeamRegion }],
    queryFn: () =>
      adminApi.get<{ companies: Company[] }>(
        activeTeamRegion
          ? `/api/companies?team_region=${encodeURIComponent(activeTeamRegion)}`
          : '/api/companies',
      ),
  });

  const invite = useMutation({
    mutationFn: (id: string) => adminApi.post(`/api/drivers/${id}/invite`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers', 'all'] }),
  });

  const drivers = driversQuery.data?.drivers ?? [];
  const companies = companiesQuery.data?.companies ?? [];
  const filtered = useMemo(
    () => filterByOwner(drivers, criteria, (d) => `${d.name} ${d.email}`),
    [drivers, criteria],
  );

  if (driversQuery.isLoading) return <CenteredSpinner />;
  if (driversQuery.error) return <ErrorMessage error={driversQuery.error} />;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Drivers</h1>
          <p className="mt-1 text-sm text-slate-500">
            Every driver — your operating company's own drivers and each contractor company's.
            Filter by owner, company, status, or search.
          </p>
        </div>
        <Btn onClick={() => setCreating(true)}>+ New driver</Btn>
      </div>

      <OwnerFilterBar
        criteria={criteria}
        onChange={setCriteria}
        companies={companies}
        statusOptions={STATUS_OPTIONS}
        searchPlaceholder="Search name or email…"
      />

      {invite.error ? <ErrorMessage error={invite.error} /> : null}

      {filtered.length === 0 ? (
        <EmptyState
          title="No drivers match"
          description="Adjust the filters, or add a driver to get started."
          action={<Btn onClick={() => setCreating(true)}>+ New driver</Btn>}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Driver</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Progress</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((d) => {
                // A driver row always opens the driver detail page — even for
                // company-owned drivers (previously these jumped to the company).
                const openTo = `/admin/drivers/${d.id}`;
                return (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={openTo} className="font-medium text-blue-700 hover:underline">
                        {d.name}
                      </Link>
                      <div className="text-xs text-slate-500">{d.email}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">
                      {d.company_id ? (d.company?.company_name ?? '—') : 'Operating company'}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{d.contractor_type ?? '—'}</td>
                    <td className="px-4 py-3">
                      <CompanyStatusBadge status={d.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="w-32">
                        <ProgressBar value={d.progress} />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => invite.mutate(d.id)}
                          loading={invite.isPending && invite.variables === d.id}
                        >
                          {d.invited_at ? 'Resend invite' : 'Invite'}
                        </Btn>
                        <Link to={openTo} className="text-sm text-blue-600 hover:underline">
                          Open
                        </Link>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {creating && <NewDriverModal companies={companies} onClose={() => setCreating(false)} />}
    </div>
  );
}

function NewDriverModal({
  companies,
  onClose,
}: {
  companies: Company[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { branding } = useBranding();
  const contractorTypes = contractorTypeOptions(branding);
  const teamRegions = teamRegionNames(useTeamRegions().data);
  const [ownerCompanyId, setOwnerCompanyId] = useState(''); // '' = Operating company
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contractorType, setContractorType] = useState('');
  const [teamRegion, setTeamRegion] = useState('');
  const isAdminOwned = ownerCompanyId === '';

  const save = useMutation({
    mutationFn: () =>
      adminApi.post('/api/drivers', {
        company_id: ownerCompanyId || undefined,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        // contractor_type and team_region are only meaningful for admin-owned
        // drivers; company drivers inherit them from their company (the API
        // nulls them either way).
        contractor_type: isAdminOwned ? contractorType || null : null,
        team_region: isAdminOwned ? teamRegion || null : null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['drivers', 'all'] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="New driver"
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Btn>
          <Btn onClick={() => save.mutate()} loading={save.isPending} disabled={!name || !email}>
            Create
          </Btn>
        </>
      }
    >
      <div className="space-y-3">
        {save.error ? <ErrorMessage error={save.error} /> : null}
        <OwnerPicker value={ownerCompanyId} onChange={setOwnerCompanyId} companies={companies} />
        <Field label="Name" required>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email" required>
          <TextInput value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Phone">
          <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
        </Field>
        {isAdminOwned && (
          <>
            <Field label="Contractor type" hint="Used to match admin-driver questionnaires.">
              <Select value={contractorType} onChange={(e) => setContractorType(e.target.value)}>
                <option value="">— none —</option>
                {contractorTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={companyInfoLabel(branding, 'teamRegion')}
              hint={
                teamRegions.length === 0
                  ? 'Add team regions in Settings to populate this dropdown.'
                  : undefined
              }
            >
              <Select
                value={teamRegion}
                onChange={(e) => setTeamRegion(e.target.value)}
                disabled={teamRegions.length === 0}
              >
                <option value="">— none —</option>
                {teamRegions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </Select>
            </Field>
          </>
        )}
      </div>
    </Modal>
  );
}
